import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import {
  Figure,
  SubjectCell,
  bacenCasesHref,
  useCodeLabel,
  useFormats,
} from '@/pages/bacen/shared';
import { fetchBacenCase } from '@/services/bacen';
import type { BacenAccused, BacenComparableGroup, AnswerOut } from '@/services/types';

/**
 * One PAS: every accused it names, each with its own penalty walk, then the
 * CRSFN acordaos the ledger references, the Copas minute where one was
 * parsed, the register entries and the comparable groups.
 *
 * Every `Answer` here is record-scoped: a refusal means THIS process lacks
 * the substrate, never that BACEN cannot support the analysis. A person is
 * rendered as BACEN publishes it (name, masked CPF) and is never a link.
 */
export function BacenCaseDetailPage() {
  const { key = '' } = useParams();
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'case', key],
    queryFn: () => fetchBacenCase(key),
    enabled: key.length > 0,
    // A 404 is an answer (no such PAS), not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });
  const money = (value: number | null) => (value === null ? '—' : currency.format(value));

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('bacen:case.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const p = data.process;
  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/bacen/cases" className="text-primary underline-offset-2 hover:underline">
            {t('bacen:case.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold tabular-nums text-foreground">
          {t('bacen:case.title', { pas: p.pas_number })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('bacen:case.subtitle', {
            accused: number.format(p.accused_count),
            citation: p.earliest_citation ?? '—',
          })}
        </p>
      </header>

      <ExplainerCard i18nKey="bacen:case.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Figure
          label={t('bacen:case.head.accused')}
          value={number.format(p.accused_count)}
          second={p.mixed_first_outcomes ? t('bacen:case.head.mixed') : undefined}
          testId="accused"
        />
        <Figure label={t('bacen:case.head.citation')} value={p.earliest_citation ?? '—'} />
        <Figure
          label={t('bacen:case.head.firstDecision')}
          value={p.earliest_first_decision ?? '—'}
        />
        <Figure label={t('bacen:case.head.boardDecision')} value={p.latest_board_decision ?? '—'} />
      </dl>

      <section className="space-y-3" data-accused-list>
        <h2 className="text-sm font-semibold text-foreground">{t('bacen:case.walk.title')}</h2>
        {data.accused.map((a) => (
          <AccusedPanel key={a.row.row_index} accused={a} money={money} />
        ))}
        <FieldGuide i18nKey="bacen:case.guide.walk" />
      </section>

      <section className="rounded-lg border bg-card p-4" data-acordaos>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:case.acordaos.title')}
        </h2>
        {data.acordaos.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('bacen:case.acordaos.none')}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.acordaos.map((a) => (
              <li
                key={`${a.row_index}-${a.acordao_key}`}
                className="rounded-md border bg-muted/30 p-3"
                data-acordao={a.acordao_key}
              >
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Link
                    to={`/bacen/crsfn/a/${a.acordao_key}`}
                    className="font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {t('bacen:case.acordaos.acordao', {
                      n: a.numero_acordao ?? a.acordao_key,
                    })}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {a.session_raw ? t('bacen:case.acordaos.session', { s: a.session_raw }) : null}
                    {a.judgment_date ? ` · ${a.judgment_date}` : null}
                  </span>
                  {a.key_conflict ? (
                    <span className="rounded border border-amber-500/50 px-1.5 text-xs text-amber-700 dark:text-amber-300">
                      {t('bacen:case.acordaos.keyConflict')}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {a.relator ? (
                    <>
                      {t('bacen:case.acordaos.relator')}{' '}
                      {a.relator_slug ? (
                        <Link
                          to={`/bacen/crsfn/r/${a.relator_slug}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {a.relator}
                        </Link>
                      ) : (
                        a.relator
                      )}
                      {' · '}
                    </>
                  ) : null}
                  {a.appeal_types.map((code) => label('appealType', code)).join(', ')}
                  {a.is_unanimous === null
                    ? null
                    : ` · ${t(a.is_unanimous ? 'bacen:case.acordaos.unanimous' : 'bacen:case.acordaos.majority')}`}
                </div>
                <div className="mt-1 text-xs text-muted-foreground" data-disposition>
                  {a.disposition_code ? (
                    <>
                      {t('bacen:case.acordaos.disposition', {
                        subject: rowName(data.accused, a.row_index),
                      })}{' '}
                      <span className="text-foreground">
                        {label('disposition', a.disposition_code)}
                      </span>
                      {a.disposition_amount !== null ? (
                        <>
                          {' · '}
                          {label('amountRole', a.disposition_amount_role)}{' '}
                          {currency.format(a.disposition_amount)}
                        </>
                      ) : null}
                    </>
                  ) : (
                    t('bacen:case.acordaos.noPartyMatch', {
                      subject: rowName(data.accused, a.row_index),
                    })
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:case.guide.acordaos" />
        </div>
      </section>

      {data.minutes.length > 0 ? (
        <section className="rounded-lg border bg-card p-4" data-minutes>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:case.minutes.title')}
          </h2>
          <ul className="space-y-2 text-sm">
            {data.minutes.map((m, i) => (
              <li
                key={i}
                className="rounded-md border bg-muted/30 p-3"
                data-minute={m.session_number}
              >
                <div className="text-xs text-muted-foreground">
                  {t('bacen:case.minutes.session', {
                    committee: m.committee,
                    n: m.session_number ?? '—',
                    date: m.session_date ?? '—',
                  })}
                  {' · '}
                  {label('parseStatus', m.parse_status)}
                </div>
                <div className="mt-1 text-foreground">{m.accused_name}</div>
                <div className="text-xs text-muted-foreground">
                  {label('minuteDisposition', m.disposition_code)}
                  {m.penalty_type ? ` · ${m.penalty_type}` : null}
                  {m.amount !== null ? ` · ${currency.format(m.amount)}` : null}
                  {m.term_years !== null
                    ? ` · ${t('bacen:cases.years', { n: m.term_years })}`
                    : null}
                  {m.legal_basis ? ` · ${m.legal_basis}` : null}
                </div>
                <blockquote className="mt-1 border-l-2 pl-2 text-xs text-muted-foreground">
                  {m.span}
                </blockquote>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.registers.length > 0 ? (
        <section className="rounded-lg border bg-card p-4" data-registers>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:case.registers.title')}
          </h2>
          <ul className="space-y-1 text-sm">
            {data.registers.map((r, i) => (
              <li key={i} className="flex flex-wrap gap-x-3 text-muted-foreground">
                <span className="font-medium text-foreground">{r.register_code}</span>
                <span>{r.subject_name}</span>
                <span className="tabular-nums">{r.masked_doc}</span>
                <span>{r.penalty}</span>
                {r.term_years !== null ? (
                  <span>{t('bacen:cases.years', { n: r.term_years })}</span>
                ) : null}
                <span className="tabular-nums">
                  {r.start_date ?? '—'} → {r.end_date ?? '—'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="space-y-3" data-comparables>
        <h2 className="text-sm font-semibold text-foreground">
          {t('bacen:case.comparables.title')}
        </h2>
        {data.comparables.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('bacen:case.comparables.none')}</p>
        ) : (
          data.comparables.map((group, i) => (
            <ComparablePanel key={i} answer={group} percent={percent} />
          ))
        )}
        <FieldGuide i18nKey="bacen:case.guide.comparables" />
      </section>
    </div>
  );
}

function rowName(accused: BacenAccused[], rowIndex: number): string {
  return accused.find((a) => a.row.row_index === rowIndex)?.row.subject_name ?? '—';
}

function AccusedPanel({
  accused,
  money,
}: {
  accused: BacenAccused;
  money: (value: number | null) => string;
}) {
  const { t } = useTranslation(['bacen']);
  const { percent } = useFormats();
  const label = useCodeLabel();
  const row = accused.row;
  return (
    <section className="rounded-lg border bg-card p-4" data-accused={row.row_index}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <SubjectCell
            name={row.subject_name}
            subjectType={row.subject_type}
            maskedCpf={row.masked_cpf}
            cnpj={row.entity_cnpj}
          />
          <div className="text-xs text-muted-foreground">
            {label('subjectType', row.subject_type)}
            {row.in_registry ? ` · ${t('bacen:case.walk.inRegistry')}` : null}
            {row.decision_office ? ` · ${row.decision_office}` : null}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div>{label('statusFamily', row.status_family)}</div>
          <div>{row.status_raw}</div>
        </div>
      </div>
      <Answer answer={accused.walk} scope="record" help={t('bacen:case.walk.help')}>
        {(walk) => (
          <div className="space-y-2">
            <ol className="grid gap-2 sm:grid-cols-4">
              {walk.stages.map((stage) => (
                <li
                  key={stage.stage}
                  className="rounded-md border bg-muted/30 p-2"
                  data-stage={stage.stage}
                >
                  <div className="text-xs text-muted-foreground">
                    {t(`bacen:case.walk.stages.${stage.stage}`, {
                      defaultValue: stage.stage,
                    })}
                  </div>
                  <div className="font-semibold text-foreground">
                    {stage.penalty_code
                      ? label('penalty', stage.penalty_code)
                      : (stage.label ?? '—')}
                  </div>
                  <div className="text-xs tabular-nums text-muted-foreground">
                    {stage.amount !== null ? money(stage.amount) : null}
                    {stage.term_years !== null
                      ? ` ${t('bacen:cases.years', { n: stage.term_years })}`
                      : null}
                  </div>
                  <div className="text-xs tabular-nums text-muted-foreground">
                    {stage.stage_date ?? '—'}
                  </div>
                </li>
              ))}
            </ol>
            <p className="text-xs text-muted-foreground" data-walk-summary>
              {label('appealKind', walk.appeal_kind)}
              {walk.trajectory ? ` · ${label('trajectory', walk.trajectory)}` : null}
              {walk.retained_share !== null
                ? ` · ${t('bacen:case.walk.retained', { share: percent.format(walk.retained_share) })}`
                : null}
              {walk.board_ruled && !walk.board_linked
                ? ` · ${t('bacen:case.walk.unlinked', { ref: row.board_reference ?? '—' })}`
                : null}
            </p>
          </div>
        )}
      </Answer>
    </section>
  );
}

function ComparablePanel({
  answer,
  percent,
}: {
  answer: AnswerOut<BacenComparableGroup>;
  percent: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['bacen']);
  const { number } = useFormats();
  const label = useCodeLabel();
  const basis = answer.value?.basis ?? (answer.reason_params?.basis as string | undefined) ?? '';
  return (
    <section className="rounded-lg border bg-card p-4" data-comparable={basis}>
      <h3 className="mb-2 text-xs font-semibold text-foreground">
        {label('basis', basis || null)}
      </h3>
      <Answer answer={answer} scope="record" help={t('bacen:case.comparables.help')}>
        {(g) => (
          <div className="space-y-2 text-sm">
            <p className="text-xs text-muted-foreground">
              {t('bacen:case.comparables.summary', {
                members: number.format(g.members),
                decided: number.format(g.decided),
                reduced: number.format(g.reduced),
                share: percent.format(g.reduced_share_of_decided),
              })}
            </p>
            <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
              {(
                [
                  ['maintained', g.maintained],
                  ['reduced', g.reduced],
                  ['eliminated', g.eliminated],
                  ['typeChanged', g.type_changed],
                  ['increased', g.increased],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="rounded-md border bg-muted/30 p-2">
                  <dt className="text-muted-foreground">{t(`bacen:overview.waterfall.${k}`)}</dt>
                  <dd className="font-semibold tabular-nums text-foreground">{number.format(v)}</dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground">
              {Object.entries(g.final_codes)
                .map(([code, n]) => `${label('penalty', code)} ${number.format(n)}`)
                .join(' · ')}
            </p>
            <Link
              to={
                g.basis === 'SAME_PAS'
                  ? bacenCasesHref({ query: g.key })
                  : bacenCasesHref(groupFilters(g.key))
              }
              className="text-xs text-primary underline-offset-2 hover:underline"
            >
              {t('bacen:case.comparables.open')}
            </Link>
          </div>
        )}
      </Answer>
    </section>
  );
}

/** "COPAS|FINE|2020" -> the case-list filters that reproduce the group. */
export function groupFilters(key: string): {
  office?: string;
  penalty_code?: string;
  year?: number;
} {
  const [office, penalty, year] = key.split('|');
  const filters: { office?: string; penalty_code?: string; year?: number } = {};
  if (office) filters.office = office;
  if (penalty) filters.penalty_code = penalty;
  if (year && /^\d{4}$/.test(year)) filters.year = Number(year);
  return filters;
}
